import { useCallback, useState } from "react";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useForm } from "react-hook-form";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { zodResolver } from "@hookform/resolvers/zod";
import { ObraForm, ObraSchema } from "../schemas/Obras_informacoesgerais/obra";
import { useLocation } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useObrasCommonReports } from "../components/hooks/obras_common_reports";
import { useObrasTopHeaderState } from "../components/hooks/obras_top_header_state";

import { ObraEstruturaTreinamentoForm } from "../schemas/Obras_treinamentos/obra_estrutura_treinamento";
import { usePlanoTreinamentoFilters } from "../hooks/Obras_treinamentos/filter";
import { usePlanoTreinamentoListaPagination } from "../hooks/Obras_treinamentos/pagination";
import { PlanoTreinamentoLista } from "../../domain/models/plano_treinamento_lista";
import { ObraHeaderInfo } from "../../domain/models/obra_header_info";

// LoadObraFuncState

type LoadObraFuncState = {
  idObra?: number;
};


//LoadTreinamentoFuncState


type LoadTreinamentoFuncState = {
  ID_PLANO_TREINAMENTO?: number;
  TIPO_TREINAMENTO?: string;
  ID_OBRA: number;
  Cargo?: string;
  page?: number;
  rowsPerPage?: number;
};



//useObrasTreinamentoViewModel
export const useObrasTreinamentoViewModel = () => {
  const {
    commons: { },
    reports: { },
    obras: {
      getObraHeaderInfoUseCase,
      getAllObraEstruturaTreinamentoUseCase,
      createObraEstruturaTreinamentoUseCase,
      deleteObraPlanoTreinamento,
      updateObraPlanoTreinamentoUseCase
    }
  } = useDependencyInjection();

  const location = useLocation();

  const backOrNavigate = useBackOrNavigate();


  const {
    pushErrorNotification,
    setPageLoading,
  } = usePageLoader();

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [idObra, setIdObra] = useState<number>();
  const [planoTreinamentoLista, setPlanoTreinamentoLista] = useState<PlanoTreinamentoLista[]>([]);
  const [selectedObra, setSelectedObra] = useState<ObraHeaderInfo>();
  const [selectedPlanoTreinamento, setSelectedPlanoTreinamento] = useState<PlanoTreinamentoLista | undefined>(undefined);
  const [planoTreinModal, setPlanoTreiModal] = useState<boolean>(false);
  const [planoTreinamentoDeleteModal, setPlanoTreinamentoDeleteModal] = useState<boolean>(false);


  const hookForm = useForm<ObraForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: { tipo: 0 },
    resolver: zodResolver(ObraSchema),
  });

  const obrasTopHeaderState = useObrasTopHeaderState();

  const [obrasReportState, initializeReportState] = useObrasCommonReports({ idObra });

  const filters = usePlanoTreinamentoFilters();
  const pagination = usePlanoTreinamentoListaPagination();

  // #region Funções de carregamento

  const loadPlanoTreinamentoListaState = useCallback(async (funcState?: LoadTreinamentoFuncState) => {
    try {
      const filteredCargo = evaluateParamValue(funcState?.Cargo, filters.Cargo)
      const filteredTIPO_TREINAMENTO = evaluateParamValue(funcState?.TIPO_TREINAMENTO, filters.TIPO_TREINAMENTO);
      const filterID_OBRA = evaluateParamValue(funcState?.ID_OBRA, filters.ID_OBRA)


      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;
      await getAllObraEstruturaTreinamentoUseCase.getAllObraEstruturaTreinamento(
        {
          IdObra: filterID_OBRA ?? 0,
          Cargo: filteredCargo ?? "",
          TipoTreinamento: filteredTIPO_TREINAMENTO ?? "",
          IdPlanoTreinamento: 0,
        },
        filteredPage,
        filteredRowsPerPage
      );
      setPlanoTreinamentoLista(getAllObraEstruturaTreinamentoUseCase.items);


      pagination.setRowsPerPage(getAllObraEstruturaTreinamentoUseCase.pageSize);
      pagination.setTotalItems(getAllObraEstruturaTreinamentoUseCase.totalItems);
    } catch (e: unknown) {
      setPlanoTreinamentoLista([]);
      throw new Error("Falha ao buscar plano de treinamento!");
    }
  }, [
    filters,
    pagination,
    getAllObraEstruturaTreinamentoUseCase,
  ]);


  const loadObraHeaderInfo = useCallback(async (id?: number) => {
    try {
      const filteredIdObra = id ?? idObra;
      const response = await getObraHeaderInfoUseCase.getObraHeaderInfo(filteredIdObra);
      setSelectedObra(response);
    } catch (e: unknown) {
      setSelectedObra(undefined);
      throw new Error('Falha ao buscar dados da obra!');
    }
  }, [idObra, getObraHeaderInfoUseCase,]);

  // #endregion Funções de carregamento


  // #region useEffect

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get('id') ?? null;
      const locationIdObra = locationId ? Number(locationId) : undefined;
      const jobs = [];

      setIdObra(locationIdObra);

      if (locationIdObra) {
        jobs.push(initializeReportState(locationIdObra));
        jobs.push(loadPlanoTreinamentoListaState({ ID_OBRA: locationIdObra }));
        jobs.push(loadObraHeaderInfo(locationIdObra));
      }
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  // #endregion useEffect


  // #region Eventos

  const handleDeleteOPlanoTreinamentoModalOpenState = useCallback((IdPlanoTreinamento: number | null, openState: boolean) => {
    if (openState) {
      if (IdPlanoTreinamento && planoTreinamentoLista) {
        setSelectedPlanoTreinamento(planoTreinamentoLista.find(x => x.IdPlanoTreinamento === IdPlanoTreinamento));
      } else {
        setSelectedPlanoTreinamento(undefined);
      }
    }
    setPlanoTreinamentoDeleteModal(openState);
  }, [
    planoTreinamentoLista,
  ]);

  const handleDeleteConfirmaPlanoTreinamento = useCallback(async () => {
    if (selectedPlanoTreinamento && idObra) {
      try {
        setSaving(true);
        await deleteObraPlanoTreinamento.deleteObraPlanoTreinamento(selectedPlanoTreinamento.IdPlanoTreinamento, idObra);
        await loadPlanoTreinamentoListaState({ ID_OBRA: idObra });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao excluir informações!'));
      } finally {
        setSaving(false);
        setPlanoTreinamentoDeleteModal(false);
      }
    }
  }, [selectedPlanoTreinamento, idObra, deleteObraPlanoTreinamento, loadPlanoTreinamentoListaState, pushErrorNotification]);

  const handlePopperOptionSelect = useCallback(async (idOption: number) => {
    switch (idOption) {
      case 1:
        setPageLoading(true);
        setTimeout(() => {
          setIdObra(undefined);
          setSelectedObra(undefined);
          setErrorMessage(undefined);
          setPlanoTreinamentoLista([]);
          setSelectedPlanoTreinamento(undefined);
          setPlanoTreiModal(false);
          setPlanoTreinamentoDeleteModal(false)

          hookForm.reset();
          setTimeout(() => setPageLoading(false), 500);
        }, 0);

        break;

      case 2:
        if (idObra) {
          // setObraDeleteModal(true);
        }
        break;
    }
  }, [
    idObra,
    hookForm,
    setPageLoading,
  ]);

  const handleReload = useCallback(async () => {
    try {
      const jobs = [];

      setLoading(true);

      if (idObra) {
        jobs.push(initializeReportState(idObra));
        jobs.push(loadPlanoTreinamentoListaState({ ID_OBRA: idObra }));
        jobs.push(loadObraHeaderInfo(idObra));
      }
      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [idObra, initializeReportState, loadObraHeaderInfo, loadPlanoTreinamentoListaState, pushErrorNotification]);

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/Obras_lista' });
  }, [
    backOrNavigate,
  ]);


  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      if (idObra) {
        usePlanoTreinamentoListaPagination.setState({ page: page + 1 });
        await loadPlanoTreinamentoListaState({
          page: page + 1,
          ID_OBRA: idObra
        });
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [idObra, loadPlanoTreinamentoListaState, pushErrorNotification]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      if (idObra) {
        setLoading(true);
        usePlanoTreinamentoListaPagination.setState({ page: 1, rowsPerPage });
        await loadPlanoTreinamentoListaState({
          page: 1, rowsPerPage,
          ID_OBRA: idObra
        });
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [idObra, loadPlanoTreinamentoListaState, pushErrorNotification]);


  // #endregion Eventos


  // #region Estrutura Treinamento
  const handleCadastroPlanoTreiOpenState = useCallback((idTreinamento: number | null, openState: boolean) => {
    if (openState) {
      if (idTreinamento) {
        setSelectedPlanoTreinamento(planoTreinamentoLista.find(x => x.IdPlanoTreinamento === idTreinamento));
      } else {
        setSelectedPlanoTreinamento(undefined);
      }
    }
    setPlanoTreiModal(openState);
  }, [planoTreinamentoLista]);

  const handlePlanoTreinamentoSubmit = useCallback(async (form: ObraEstruturaTreinamentoForm) => {
    setSaving(true);
    if (selectedObra && idObra) {
      try {
        if (selectedPlanoTreinamento) {
          await updateObraPlanoTreinamentoUseCase.updateObraPlanoTreinamento(
            idObra,
            selectedPlanoTreinamento.IdPlanoTreinamento,
            form.cargo,
            form.TIPO_TREINAMENTO,
          );
        } else
          await createObraEstruturaTreinamentoUseCase.createObraPlanoTreinamento(idObra, form.TIPO_TREINAMENTO, form.cargo)

        await loadPlanoTreinamentoListaState({ ID_OBRA: idObra });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao criar estrutura organizacional!'));
      } finally {
        setSaving(false);
        setPlanoTreiModal(false);
      }
    }
  }, [createObraEstruturaTreinamentoUseCase, idObra, loadPlanoTreinamentoListaState, pushErrorNotification, selectedObra, selectedPlanoTreinamento, updateObraPlanoTreinamentoUseCase]);



  return {
    loading,
    saving,
    errorMessage,
    hookForm,
    idObra,
    selectedObra: selectedObra ?? null,
    obrasTopHeaderState,
    obrasReportState,
    planoTreinamentoLista: planoTreinamentoLista,
    filters,
    pagination,
    planoTreinModal,
    planoTreinamentoDeleteModal,
    selectedPlanoTreinamento,
    dropdown: {
      cargoDropdown: filters.Cargo ?? [],
      TIPO_TREINAMENTODropdown: filters.TIPO_TREINAMENTO ?? [],
    },

    handlePopperOptionSelect,
    handleReload,
    handleBack,
    handleCadastroPlanoTreiOpenState,
    handlePlanoTreinamentoSubmit,
    handleDeleteOPlanoTreinamentoModalOpenState,
    handleDeleteConfirmaPlanoTreinamento,
    handlePageChange,
    handleRowsPerPageChange,
  };
}